import { createFileRoute } from "@tanstack/react-router";
import { Footer } from "@/components/footer";
import { PageHeader } from "@/components/page-header";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { whatsNewAudienceLabels, whatsNewUpdates, type WhatsNewUpdate } from "@/types/whats-new";

const formatShortDate = (date: string) =>
  new Date(date + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatLongDate = (date: string) =>
  new Date(date + "T00:00:00").toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const updateBodyClassName = "text-base leading-7 text-foreground/85";
const updateSectionTitleClassName = "mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground";
const updateListClassName = "gap-y-2.5 text-foreground/85";
const updateStrongClassName = "font-semibold text-foreground";

const UpdateBullet = ({ children }: { children: React.ReactNode }) => (
  <li className="flex items-start gap-3">
    <span className="mt-2.5 size-1.5 flex-shrink-0 rounded-full bg-accent"></span>
    <div>{children}</div>
  </li>
);

interface SidebarProps {
  updates: WhatsNewUpdate[];
}

const WhatsNewSidebar: React.FC<SidebarProps> = ({ updates }) => (
  <aside className="hidden w-56 shrink-0 lg:block">
    <div className="sticky top-24">
      <p className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Recent Updates</p>
      <nav aria-label="What's New Navigation" className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-2">
        <ul className="border-l border-border">
          {updates.map((update) => (
            <li key={update.id}>
              <a
                href={`#${update.id}`}
                className="group -ml-px block border-l-2 border-transparent py-3 pl-4 pr-2 transition-colors hover:border-accent hover:bg-accent/5 target:border-accent target:bg-accent/5"
              >
                <div className="text-sm font-medium leading-snug text-accent transition-colors group-hover:text-accent/90">
                  {update.title}
                </div>
                <time
                  className="mt-1 block text-xs uppercase tracking-wide text-muted-foreground"
                  dateTime={update.date}
                >
                  {formatShortDate(update.date)}
                </time>
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  </aside>
);

interface ContentProps {
  updates: WhatsNewUpdate[];
}

const WhatsNewContent: React.FC<ContentProps> = ({ updates }) => {
  const sorted = [...updates].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <section className="max-w-4xl">
      <div className="flex flex-col gap-y-8 md:gap-y-10">
        {sorted.map((update, index) => (
          <WhatsNewItem key={update.id} update={update} isFirst={index === 0} />
        ))}
      </div>
    </section>
  );
};

interface ItemProps {
  update: WhatsNewUpdate;
  isFirst?: boolean;
}

const WhatsNewItem: React.FC<ItemProps> = ({ update, isFirst }) => (
  <article id={update.id} className={cn("scroll-mt-24", !isFirst && "border-t border-border pt-10")}>
    <div className="mb-6">
      <time
        className="mb-3 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        dateTime={update.date}
      >
        {formatLongDate(update.date)}
      </time>
      <h2 className="text-2xl font-semibold leading-tight text-foreground md:text-[1.6rem]">{update.title}</h2>
      {update.audiences && (
        <ul aria-label="Relevant audiences" className="mt-3 flex flex-wrap gap-2">
          {update.audiences.map((audience) => (
            <li key={audience}>
              <Badge variant="secondary">{whatsNewAudienceLabels[audience]}</Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
    <div className="flex flex-col gap-y-4">
      <p className={cn(updateBodyClassName, "mb-6")}>{update.summary}</p>
      <div className="mb-6">
        <h4 className={updateSectionTitleClassName}>What's new:</h4>
        <ul className={updateListClassName}>
          {update.bullets.map((bullet) => (
            <UpdateBullet key={bullet.id}>
              <strong className={updateStrongClassName}>{bullet.title}:</strong> {bullet.description}
            </UpdateBullet>
          ))}
        </ul>
      </div>
    </div>
  </article>
);

function WhatsNewPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader title="What's New" />

      <div className="container flex flex-1 flex-col gap-8 px-4 py-10 lg:flex-row lg:gap-12">
        <WhatsNewSidebar updates={whatsNewUpdates} />
        <main className="min-w-0 flex-1">
          <WhatsNewContent updates={whatsNewUpdates} />
        </main>
      </div>
      <Footer />
    </div>
  );
}
export const Route = createFileRoute("/_marketing/whats-new")({
  component: WhatsNewPage,
});
